import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, Fragment, ReactNode, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  areaWaypointReactKey,
  IAreaWaypoint,
  ICoordinate,
  IGeoJSON,
  MarkerIconType,
  TAreaWaypointDirection,
} from "@ryde/shared";

import {
  MapLibreAutoPanPopup,
  MapLibreMapHost,
  MapLibreStationPinMarker,
  useMapLibreLocationsAutoFit,
} from "../../../common/map/maplibre";
import { AreaWaypointMapPopupStyled } from "./area-waypoints.styled";
import {
  areaWaypointVisual,
  getAreaWaypointsMapFitLocations,
} from "./area-waypoints.utils";

interface IAreaWaypointsMapProps {
  waypoints: IAreaWaypoint[];
  direction: TAreaWaypointDirection;
  polygon?: IGeoJSON | null;
  onWaypointDragEnd?: (params: { id: number } & ICoordinate) => void;
  className?: string;
  children?: ReactNode;
}

export const AreaWaypointsMap: FC<IAreaWaypointsMapProps> = ({
  waypoints,
  direction,
  polygon,
  onWaypointDragEnd,
  className,
  children,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [openWaypointId, setOpenWaypointId] = useState<number | null>(null);
  const fitLocations = useMemo(
    () => getAreaWaypointsMapFitLocations({ waypoints, polygon }),
    [waypoints, polygon],
  );

  useMapLibreLocationsAutoFit({
    mapRef,
    locations: fitLocations,
    isMapLoaded,
  });

  const handlePopupClose = useEvent(() => {
    setOpenWaypointId(null);
  });

  return (
    <MapLibreMapHost
      className={className}
      mapRef={mapRef}
      isFullscreenEnabled
      onLoadedChange={setIsMapLoaded}
    >
      {isMapLoaded &&
        waypoints.map(waypoint => {
          const displayAddress =
            waypoint.fullAddress?.trim() || waypoint.name || "";
          const popupTitle = waypoint.name || displayAddress;
          const isPopupOpen = openWaypointId === waypoint.id;
          const markerKey = areaWaypointReactKey(direction, waypoint.id);

          const handleMarkerClick = (event: {
            originalEvent: MouseEvent;
          }): void => {
            event.originalEvent.stopPropagation();
            setOpenWaypointId(current =>
              current === waypoint.id ? null : waypoint.id,
            );
          };

          return (
            <Fragment key={markerKey}>
              <MapLibreStationPinMarker
                latitude={waypoint.latitude}
                longitude={waypoint.longitude}
                type={MarkerIconType.Point}
                color={areaWaypointVisual.color}
                label="W"
                onClick={handleMarkerClick}
                title={isPopupOpen ? undefined : popupTitle}
                actionLabel={popupTitle}
                isDraggable={Boolean(onWaypointDragEnd)}
                onDragEnd={
                  onWaypointDragEnd
                    ? coordinate =>
                        onWaypointDragEnd({
                          id: waypoint.id,
                          ...coordinate,
                        })
                    : undefined
                }
              />
              <MapLibreAutoPanPopup
                isOpen={isPopupOpen}
                latitude={waypoint.latitude}
                longitude={waypoint.longitude}
                onClose={handlePopupClose}
              >
                <AreaWaypointMapPopupStyled>
                  <div
                    className="area-waypoint-map-popup-title"
                    title={popupTitle}
                  >
                    {popupTitle}
                  </div>
                  {displayAddress && displayAddress !== popupTitle && (
                    <div
                      className="area-waypoint-map-popup-address"
                      title={displayAddress}
                    >
                      {displayAddress}
                    </div>
                  )}
                </AreaWaypointMapPopupStyled>
              </MapLibreAutoPanPopup>
            </Fragment>
          );
        })}
      {children}
    </MapLibreMapHost>
  );
};
